import { VoidFunc } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';
import { connect } from 'react-redux';
import { MemoryRouter } from 'react-router-dom';

import { ReduxState } from '../../../../store';
import { isValidLatLng } from '../../../../util/geoUtil';
import InfoPane from '../InfoPane';
import { AdvancedMarkerWrapper } from './AdvancedMarkerWrapper';
import { calculateArrowStyles, getArrowWrapperClass } from './arrowPositioningUtils';
import {
  calculateClearanceInAllDirections,
  generatePositionForDirection,
  InfoPanePositioning,
  selectBestDirection,
} from './infoPanePositioningUtils';
import {
  INFOPANE_FALLBACK_HEIGHT,
  INFOPANE_FALLBACK_WIDTH,
  POSITION_CHANGE_TOLERANCE,
  Z_INDEX_INFOPANE_TOP,
} from './markerConstants';
import styles from './TTMarker.module.css';
import { noop } from '../../../../util/appUtil';

/**
 * TODO: This file (953 lines) exceeds the 250-line guideline for maintainability.
 * Consider refactoring into focused modules:
 * 1. Overlay lifecycle management (onAdd/onRemove/draw) → helper class or hooks
 * 2. Positioning calculations → already extracted to infoPanePositioningUtils
 * 3. Arrow positioning → already extracted to arrowPositioningUtils
 * 4. Event listener management → could be extracted to custom hook
 * 5. Render methods → could be split into separate components
 * This would improve maintainability and reduce cognitive load.
 */
export class TTMarker extends Component<TTMarkerProps, TTMarkerState> {
  positionRef: React.RefObject<HTMLDivElement>;

  infoPaneRef: React.RefObject<HTMLDivElement>;

  arrowRef: React.RefObject<HTMLDivElement>;

  // wrapper ref that will be positioned (left/top). inner arrow holds rotation-only styles
  arrowWrapperRef: React.RefObject<HTMLDivElement>;

  markerContainerRef: HTMLDivElement | null = null;

  overlayContentContainer: HTMLDivElement | null = null;

  overlayContentRoot: ReturnType<typeof createRoot> | null = null;

  overlay: google.maps.OverlayView | null = null;

  infoPaneRoot: ReturnType<typeof createRoot> | null = null;

  labelRoot: ReturnType<typeof createRoot> | null = null;

  private hoverHideTimer?: number;

  // Map event listeners for InfoWindow-like behavior
  private mapEventListeners: google.maps.MapsEventListener[] = [];

  private repositionTimeout?: number;

  private lastMeasuredDimensions: { width: number; height: number } | null = null;

  constructor(props: TTMarkerProps) {
    super(props);
    this.positionRef = React.createRef();
    this.infoPaneRef = React.createRef();
    this.arrowRef = React.createRef();
    this.arrowWrapperRef = React.createRef();
    this.state = {
      showLabelInfo: !this.props.isLabelHover,
      isHovered: false,
    };
  }

  componentDidMount() {
    this.createOrUpdateOverlay();
    this.setupMapEventListeners();
  }

  componentDidUpdate(prevProps: TTMarkerProps) {
    // Update overlay when selection changes
    if (prevProps.isSelected !== this.props.isSelected) {
      // CRITICAL: When selection state changes, we must recreate the overlay
      // to move it between Google Maps panes (overlayMouseTarget vs floatPane)
      // This ensures InfoPanes (floatPane) always render above description labels (overlayMouseTarget)
      if (this.overlay) {
        // Store reference and clear instance variable immediately
        const ov = this.overlay;
        this.overlay = null;

        // Remove overlay from map synchronously
        try {
          ov.setMap(null);
        } catch (e) {
          // Ignore removal errors
        }

        // Note: Don't call cleanupRoots() here - let onRemove handle it
        // The roots will be cleaned up by the overlay's onRemove callback
      }

      this.createOrUpdateOverlay();

      // Manage map event listeners based on selection state
      if (this.props.isSelected && !prevProps.isSelected) {
        // Just became selected - set up listeners and immediately position
        this.setupMapEventListeners();
        // Immediate positioning when infopane opens
        requestAnimationFrame(() => {
          this.updateInfoPanePosition();
        });
      } else if (!this.props.isSelected && prevProps.isSelected) {
        // Just became unselected - clean up listeners
        this.cleanupMapEventListeners();
      }
    }

    // Proactive positioning updates for position/content changes
    // Use tolerance-based comparison for position to handle floating point precision and object recreation
    const prevLat = prevProps.position?.lat ?? 0;
    const prevLng = prevProps.position?.lng ?? 0;
    const currLat = this.props.position?.lat ?? 0;
    const currLng = this.props.position?.lng ?? 0;

    const positionChanged =
      Math.abs(prevLat - currLat) > POSITION_CHANGE_TOLERANCE ||
      Math.abs(prevLng - currLng) > POSITION_CHANGE_TOLERANCE;
    const contentChanged =
      prevProps.infoPaneContent !== this.props.infoPaneContent ||
      prevProps.infoPaneTitle !== this.props.infoPaneTitle;
    const selectionChanged = prevProps.isSelected !== this.props.isSelected;

    // Update overlay for prop changes (but skip if selection already triggered overlay recreation)
    if (
      !selectionChanged &&
      (prevProps.label !== this.props.label || positionChanged || contentChanged)
    ) {
      this.createOrUpdateOverlay();

      // If infopane is open and position changed, immediately reposition
      if (this.props.isSelected && (positionChanged || contentChanged)) {
        requestAnimationFrame(() => {
          this.updateInfoPanePosition();
        });
      }
    }

    const shouldCloseInfoPane = selectionChanged && this.props.isSelected === false;
    if (shouldCloseInfoPane) {
      requestAnimationFrame(() => {
        this.handleMouseLeave();
      });
    }

    // Update event listeners if map reference changes
    if (prevProps.mapRef !== this.props.mapRef) {
      this.cleanupMapEventListeners();
      if (this.props.isSelected) {
        this.setupMapEventListeners();
      }
    }
  }

  componentWillUnmount() {
    if (this.hoverHideTimer) {
      window.clearTimeout(this.hoverHideTimer);
      this.hoverHideTimer = undefined;
    }

    this.cleanupMapEventListeners();

    requestAnimationFrame(() => {
      this.cleanupRoots();

      if (!this.overlay) {
        return;
      }
      try {
        this.overlay?.setMap(null);
      } finally {
        this.overlay = null;
      }
    });
  }

  private cleanupRoots = () => {
    try {
      if (this.labelRoot) this.labelRoot.unmount();
      if (this.infoPaneRoot) this.infoPaneRoot.unmount();
    } finally {
      this.labelRoot = null;
      this.infoPaneRoot = null;
    }
  };

  /**
   * Gets actual InfoPane dimensions from the DOM or returns defaults
   */
  private getInfoPaneDimensions = (): { width: number; height: number } => {
    // Try multiple methods to get actual dimensions

    // Method 1: Direct ref measurement
    if (this.infoPaneRef?.current) {
      const bounds = this.infoPaneRef.current.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        return { width: bounds.width, height: bounds.height };
      }
    }

    // Method 2: Query the rendered InfoPane element
    if (this.overlayContentContainer) {
      // Look for the actual InfoPane content (not the wrapper)
      const infoPaneContent = this.overlayContentContainer.querySelector(
        '[data-test*="info-pane"], .info-pane-content, .infoBox > div',
      );
      if (infoPaneContent) {
        const bounds = infoPaneContent.getBoundingClientRect();
        if (bounds.width > 0 && bounds.height > 0) {
          return { width: bounds.width, height: bounds.height };
        }
      }
    }

    // Method 3: Use stored dimensions if we've measured before
    if (this.lastMeasuredDimensions) {
      return this.lastMeasuredDimensions;
    }

    // Default fallback dimensions
    return { width: INFOPANE_FALLBACK_WIDTH, height: INFOPANE_FALLBACK_HEIGHT };
  };

  /**
   * Calculates optimal InfoPane positioning using fit-based algorithm.
   *
   * Algorithm:
   * 1. Measures InfoPane dimensions (cached for performance)
   * 2. Projects marker lat/lng to pixel coordinates
   * 3. Calculates clearance in all four directions (above/below/left/right)
   * 4. Selects direction with most available space
   * 5. Falls back to best-fit ratio if nothing fits well
   *
   * @returns Transform CSS, arrow class, and selected position
   */
  private calculateInfoWindowPosition = (): {
    infoPanePosition: InfoPanePositioning;
    arrowClass: string;
    position: 'above' | 'below' | 'left' | 'right';
  } => {
    const { mapRef, position } = this.props;
    const infoPaneOffset = this.props.infoPaneOffsetY ?? 25;
    const { width: panelWidth } = this.getInfoPaneDimensions();

    const defaultResult = {
      infoPanePosition: {
        left: `${-panelWidth / 2}px`,
        bottom: `${infoPaneOffset}px`,
      },
      arrowClass: styles.arrowWrapperBottom,
      position: 'above' as 'above',
    };

    if (!mapRef || position.lat == null || position.lng == null || !this.overlay) {
      return defaultResult;
    }

    try {
      const projection = this.overlay.getProjection();
      if (!projection) return defaultResult;

      const markerLatLng = new google.maps.LatLng(position.lat, position.lng);
      const markerPixel = projection.fromLatLngToDivPixel(markerLatLng);
      if (!markerPixel) return defaultResult;

      const mapDiv = mapRef.getDiv();
      const mapBounds = mapDiv.getBoundingClientRect();
      const { width: mapWidth, height: mapHeight } = mapBounds;
      const { width: paneWidth, height: paneHeight } = this.getInfoPaneDimensions();

      // Store dimensions for future use
      this.lastMeasuredDimensions = { width: paneWidth, height: paneHeight };

      // ========================================================================
      // CRITICAL: Google Maps Projection Coordinate System vs. Screen Position
      // ========================================================================
      //
      // Google Maps uses an INTERNAL COORDINATE SYSTEM that doesn't directly
      // correspond to the visible screen position of markers. The projection's
      // fromLatLngToDivPixel() method returns coordinates in the map's full
      // world coordinate space, NOT the visible viewport coordinates.
      //
      // Example Problem:
      // - Marker visually appears at bottom-right corner of map
      // - projection.fromLatLngToDivPixel() returns (892, 597)
      // - Actual screen position is (1765, 1213) relative to map container
      // - Difference: ~873px horizontal, ~616px vertical!
      //
      // This mismatch occurs because:
      // 1. Google Maps internally renders a larger canvas than the visible area
      // 2. The projection coordinates are relative to the world coordinate system
      // 3. The visible viewport is just a window into that larger coordinate space
      // 4. Panning/zooming changes the relationship between world and screen coords
      //
      // Why This Matters for InfoPanel Positioning:
      // - Clearance calculations MUST use actual screen position
      // - Using projection coords calculates wrong available space
      // - Results in InfoPanel positioning to wrong side (e.g., right when left has space)
      //
      // Solution:
      // - Get actual DOM element position via getBoundingClientRect()
      // - Calculate position relative to map container bounds
      // - Use these screen coordinates for clearance/positioning calculations
      // - Fall back to projection coords only if DOM ref unavailable
      // ========================================================================

      let markerX = markerPixel.x; // Fallback: projection coordinates
      let markerY = markerPixel.y;

      // Get the marker's actual screen position relative to the map container
      if (this.markerContainerRef) {
        const markerScreenBounds = this.markerContainerRef.getBoundingClientRect();

        // Calculate position relative to map container (screen coordinates)
        // markerScreenBounds gives absolute viewport position
        // mapBounds gives map container's absolute viewport position
        // Subtracting gives us marker position within the map container
        const screenX = markerScreenBounds.left - mapBounds.left;
        const screenY = markerScreenBounds.top - mapBounds.top;

        // CRITICAL: Use actual screen coordinates for positioning calculations
        // This ensures clearance is calculated based on where the marker
        // actually appears on screen, not where the projection system thinks it is
        markerX = screenX;
        markerY = screenY;
      }

      // Calculate clearance and select best direction using utilities
      const clearance = calculateClearanceInAllDirections(
        markerX,
        markerY,
        mapWidth,
        mapHeight,
        paneWidth,
        paneHeight,
        infoPaneOffset,
      );
      const bestDirection = selectBestDirection(
        clearance,
        markerX,
        markerY,
        mapWidth,
        mapHeight,
        paneWidth,
        paneHeight,
        infoPaneOffset,
      );

      // Generate transform and arrow class for selected direction
      const infoPanePosition = generatePositionForDirection(
        bestDirection,
        paneHeight,
        paneWidth,
        infoPaneOffset,
      );
      const arrowClass = getArrowWrapperClass(bestDirection, styles);

      return {
        infoPanePosition,
        arrowClass,
        position: bestDirection,
      };
    } catch (error) {
      // Positioning calculation failed - fall back to default (above)
      return defaultResult;
    }
  };

  getInfoPane = () => {
    const { infoPaneContent, infoPaneTitle, truckHasTicket, infoPaneZIndex } = this.props;

    // Calculate InfoWindow-like positioning with edge detection
    const { infoPanePosition, arrowClass } = this.calculateInfoWindowPosition();

    // CRITICAL: InfoPane z-index must be higher than all description labels
    // Default to Z_INDEX_INFOPANE_TOP (500) to ensure it appears above markers
    // Allow custom override if provided, but enforce minimum of Z_INDEX_INFOPANE_TOP
    const guaranteedInfoPaneZIndex = Math.max(
      infoPaneZIndex ?? Z_INDEX_INFOPANE_TOP,
      Z_INDEX_INFOPANE_TOP,
    );

    const infoPane = (
      <div
        style={{
          position: 'absolute',
          width: '0px',
          height: '0px',
          overflow: 'visible',
          pointerEvents: 'all',
        }}
        ref={this.positionRef}
      >
        <div
          className={cx({
            [styles.infoBox]: true,
          })}
          style={{
            position: 'absolute',
            ...infoPanePosition,
            zIndex: guaranteedInfoPaneZIndex,
            minWidth: 400,
            maxWidth: 500,
          }}
          ref={this.infoPaneRef}
        >
          <InfoPane
            onCloseClick={this.onCloseClick}
            onLogoutClick={this.onLogoutClickHandle}
            truckHasTicket={truckHasTicket}
            infoPaneTitle={infoPaneTitle}
            infoPaneContent={infoPaneContent}
            isExternal={this.props.isExternal}
          />
          <div ref={this.arrowWrapperRef} className={arrowClass}>
            <div ref={this.arrowRef} className={styles.arrow} />
          </div>
        </div>
      </div>
    );

    return infoPane;
  };

  centerMap = (markerPosition?: any) => {
    const { mapRef } = this.props;

    if (!markerPosition || !isValidLatLng(markerPosition.lat, markerPosition.lng)) {
      return;
    }

    const centerPosition = {
      lat: markerPosition.lat,
      lng: markerPosition.lng,
    };

    if (mapRef) {
      mapRef.setCenter(centerPosition);
    }
  };

  createOrUpdateOverlay = () => {
    const { mapRef, position, label, isSelected, pixelOffset, isRouteHover } = this.props;
    if (!mapRef) {
      return;
    }

    // Remove overlay if no map or nothing to show
    if (!isRouteHover && !label && !isSelected) {
      if (!this.overlay) {
        return;
      }
      const ov = this.overlay;
      this.overlay = null;
      // Defer removal to avoid onRemove firing during current React commit
      requestAnimationFrame(() => {
        try {
          ov.setMap(null);
        } finally {
          // also clean up any legacy roots after map detaches overlay
          this.cleanupRoots();
        }
      });
    }

    const positionLiteral = {
      lat: position.lat ?? 0,
      lng: position.lng ?? 0,
    };

    // Create new overlay if needed
    if (!this.overlay) {
      // creating new overlay
      this.overlay = new google.maps.OverlayView();
      const ov = this.overlay;
      // Capture refs in closure to avoid race conditions with instance variables
      let localMarkerContainer: HTMLDivElement | null = null;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let localContentContainer: HTMLDivElement | null = null;
      let localContentRoot: ReturnType<typeof createRoot> | null = null;

      ov.onAdd = () => {
        const panes = ov.getPanes();
        if (!panes) return;

        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.className = styles.infoBoxWrapper;

        if (pixelOffset) {
          container.style.left = `${pixelOffset.width}px`;
          container.style.top = `${pixelOffset.height}px`;
        }

        // attach to panes and store refs
        localMarkerContainer = container;
        this.markerContainerRef = container;
        // CRITICAL: Use different Google Maps panes to ensure proper layering
        // Google Maps pane order (bottom to top): mapPane < overlayLayer < overlayMouseTarget < floatPane
        // - InfoPanes (selected): Use floatPane (highest pane, where info windows belong)
        // - Description labels (unselected): Use overlayMouseTarget (lower pane for interactive elements)
        // This DOM separation ensures InfoPanes structurally render above description labels
        const targetPane = isSelected ? panes.floatPane : panes.overlayMouseTarget;
        if (!targetPane) {
          console.error(
            '[TTMarker] Target pane not available:',
            isSelected ? 'floatPane' : 'overlayMouseTarget',
          );
          return;
        }
        targetPane.appendChild(container);

        // Create a persistent child container and root; we'll switch content via render()
        const contentContainer = document.createElement('div');
        localMarkerContainer.appendChild(contentContainer);
        const contentRoot = createRoot(contentContainer);

        // Capture in closure variables
        localContentContainer = contentContainer;
        localContentRoot = contentRoot;

        // Store in instance variables for updateOverlayContent to use
        this.overlayContentContainer = contentContainer;
        this.overlayContentRoot = contentRoot;

        // Render initial content after container is created
        this.updateOverlayContent();
      };

      ov.draw = () => {
        // use the captured ov and localMarkerContainer so draw is safe even if this.overlay is cleared
        try {
          const projection = ov.getProjection();
          if (!projection || !localMarkerContainer) return;

          // Use fromLatLngToDivPixel for overlay positioning
          // This gives us pixel coordinates relative to the map's div container
          const markerLatLng = new google.maps.LatLng(positionLiteral.lat, positionLiteral.lng);
          const point = projection.fromLatLngToDivPixel(markerLatLng);

          if (point) {
            // Position the container at the exact marker location
            localMarkerContainer.style.left = `${point.x}px`;
            localMarkerContainer.style.top = `${point.y}px`;

            // If InfoPane is showing, update its position
            if (this.props.isSelected) {
              // Calculate once and reuse the result to avoid inconsistencies between calls
              const { infoPanePosition, position } = this.calculateInfoWindowPosition();

              requestAnimationFrame(() => {
                if (this.infoPaneRef?.current) {
                  this.infoPaneRef.current.style.top = infoPanePosition.top ?? '';
                  this.infoPaneRef.current.style.bottom = infoPanePosition.bottom ?? '';
                  this.infoPaneRef.current.style.left = infoPanePosition.left ?? '';
                  this.infoPaneRef.current.style.right = infoPanePosition.right ?? '';
                }
                // Position the arrow in the same frame. positionArrowOnly will run synchronously when defer=false
                this.positionArrowOnly(position, false);
              });
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Error in overlay draw:', error);
          }
        }
      };

      ov.onRemove = () => {
        // Clean up container from DOM using local reference
        if (localMarkerContainer?.parentElement) {
          try {
            localMarkerContainer.parentElement.removeChild(localMarkerContainer);
          } catch (_) {
            // Container may already be removed
          }
          localMarkerContainer = null;
          this.markerContainerRef = null;
        }

        // Clean up React roots using LOCAL closure variables to avoid race conditions
        // CRITICAL: Must use localContentRoot (not this.overlayContentRoot) to prevent
        // old overlay's cleanup from unmounting new overlay's root when overlays are
        // rapidly recreated (e.g., selection changes)
        requestAnimationFrame(() => {
          try {
            if (localContentRoot) {
              localContentRoot.unmount();
            }
          } finally {
            localContentRoot = null;
            localContentContainer = null;
            // Only clear instance variables if they match the local refs
            // This prevents clearing a NEW overlay's refs when old overlay cleans up
            if (this.overlayContentRoot === localContentRoot) {
              this.overlayContentRoot = null;
              this.overlayContentContainer = null;
            }
          }
          this.cleanupRoots();
        });
      };

      ov.setMap(mapRef as google.maps.Map);
    } else {
      // Update existing overlay only if container exists
      // If container doesn't exist yet, onAdd will handle initial render
      if (this.markerContainerRef) {
        this.updateOverlayContent();
      }
      if (!this.markerContainerRef) {
        requestAnimationFrame(() => {
          if (this.markerContainerRef) {
            this.updateOverlayContent();
          }
        });
      }
    }
  };

  /**
   * Setup map event listeners to reposition the InfoPane during map interactions
   */
  private setupMapEventListeners = () => {
    const { mapRef } = this.props;
    if (!mapRef || !this.overlay) return;

    const areListenersSetup = this.mapEventListeners && this.mapEventListeners.length > 0;
    if (areListenersSetup) return;

    const add = (eventName: string) => {
      try {
        let listener: any = null;
        if ((mapRef as any).addListener) {
          listener = (mapRef as any).addListener(eventName, () => {
            // Debounce rapid changes
            if (this.repositionTimeout) window.clearTimeout(this.repositionTimeout);
            this.repositionTimeout = window.setTimeout(() => {
              this.updateInfoPanePosition();
              this.repositionTimeout = undefined;
            }, 50);
          });
        }

        if (listener) this.mapEventListeners.push(listener);
      } catch (_) {
        // ignore
      }
    };

    // Listen to essential map events only to reduce overhead
    // - zoom_changed: Map zoom level changes
    // - dragend: After user finishes panning (not during)
    // - idle: After map settles from any movement/zoom
    // - projection_changed: Coordinate system changes
    ['zoom_changed', 'dragend', 'idle', 'projection_changed'].forEach(add);
  };

  /**
   * Remove any map event listeners we've attached
   */
  private cleanupMapEventListeners = () => {
    try {
      this.mapEventListeners.forEach((l: any) => {
        try {
          if (l && typeof l.remove === 'function') {
            l.remove();
          } else if (l) {
            const g = (window as any).google;
            if (g && g.maps && g.maps.event && typeof g.maps.event.removeListener === 'function') {
              g.maps.event.removeListener(l);
            }
          }
        } catch (_) {
          // ignore
        }
      });
    } finally {
      this.mapEventListeners = [];
      if (this.repositionTimeout) {
        window.clearTimeout(this.repositionTimeout);
        this.repositionTimeout = undefined;
      }
    }
  };

  /**
   * Recalculate and apply InfoPane position (measures DOM when possible)
   */
  private updateInfoPanePosition = () => {
    if (!this.overlay) return;
    try {
      // Force a draw so projection/positions are up-to-date
      try {
        this.overlay.draw();
      } catch (_) {
        // ignore draw errors
      }

      // Measure the InfoPane DOM and update cache
      if (this.infoPaneRef?.current) {
        const bounds = this.infoPaneRef.current.getBoundingClientRect();
        if (bounds.width > 0 && bounds.height > 0) {
          this.lastMeasuredDimensions = { width: bounds.width, height: bounds.height };
        }
      }

      // Calculate once outside RAF to avoid recalculation
      const { infoPanePosition, position } = this.calculateInfoWindowPosition();

      requestAnimationFrame(() => {
        if (this.infoPaneRef?.current) {
          this.infoPaneRef.current.style.top = infoPanePosition.top ?? '';
          this.infoPaneRef.current.style.bottom = infoPanePosition.bottom ?? '';
          this.infoPaneRef.current.style.left = infoPanePosition.left ?? '';
          this.infoPaneRef.current.style.right = infoPanePosition.right ?? '';
        }
        // Position arrow synchronously in this frame (defer=false)
        this.positionArrowOnly(position, false);
      });
    } catch (_) {
      // ignore
    }
  };

  /**
   * Positions the arrow wrapper element to point at the marker.
   *
   * Architecture:
   * - CSS classes handle rotation and edge attachment (via top/bottom/left/right)
   * - JavaScript centers the arrow along the perpendicular axis
   * - For above/below: centers horizontally, CSS handles vertical
   * - For left/right: centers vertically, CSS handles horizontal
   *
   * @param position - Direction InfoPane is positioned relative to marker
   * @param defer - Whether to defer execution to next animation frame
   *
   * CRITICAL: Must use cached dimensions (not getBoundingClientRect) because
   * this runs in same RAF as InfoPane transform, before browser paint.
   */
  private positionArrowOnly = (
    position?: 'above' | 'below' | 'left' | 'right',
    defer: boolean = true,
  ) => {
    if (!this.arrowWrapperRef?.current || !this.lastMeasuredDimensions) return;

    const run = () => {
      try {
        const wrapperEl = this.arrowWrapperRef!.current!;
        const panelW = this.lastMeasuredDimensions!.width;
        const panelH = this.lastMeasuredDimensions!.height;

        // Set CSS class to apply rotation for this position
        const arrowClass = getArrowWrapperClass(position || 'above', styles);
        wrapperEl.className = arrowClass;

        // Calculate and apply arrow position and styles
        const arrowStyles = calculateArrowStyles(position || 'above', panelW, panelH);
        // Clear unused axis and set active axis
        if (arrowStyles.left !== undefined) {
          wrapperEl.style.left = arrowStyles.left;
          wrapperEl.style.top = ''; // Clear vertical positioning
        } else if (arrowStyles.top !== undefined) {
          wrapperEl.style.top = arrowStyles.top;
          wrapperEl.style.left = ''; // Clear horizontal positioning
        }
        wrapperEl.style.width = arrowStyles.width;
        wrapperEl.style.height = arrowStyles.height;
        wrapperEl.style.pointerEvents = 'all';
      } catch (_) {
        // ignore
      }
    };

    if (defer) {
      requestAnimationFrame(run);
    } else {
      run();
    }
  };

  private cancelHideLabel = (): void => {
    if (this.hoverHideTimer) {
      window.clearTimeout(this.hoverHideTimer);
      this.hoverHideTimer = undefined;
    }
  };

  // Delayed hide gives the cursor a grace window to transit between the
  // icon and the label without flicker. Re-entering either surface calls
  // cancelHideLabel, which clears the timer before it fires.
  private scheduleHideLabel = (): void => {
    this.cancelHideLabel();
    this.hoverHideTimer = window.setTimeout(() => {
      this.hoverHideTimer = undefined;
      if (this.state.showLabelInfo) {
        this.setState({ showLabelInfo: false }, () => this.updateOverlayContent());
      }
    }, 220);
  };

  private onMarkerClick = (): void => this.props.onClick?.();

  private showLabel = (): void => {
    if (!this.state.showLabelInfo) this.setState({ showLabelInfo: true });
  };

  private hideLabel = (): void => {
    if (this.state.showLabelInfo) this.setState({ showLabelInfo: false });
  };

  private onLogoutClickHandle = (): void => this.props.onLogoutDriver?.();

  private onCloseClick = (): void => {
    this.setState({ isHovered: false }, () => {
      this.updateOverlayContent();
    });
    this.props.onCloseClick?.();
  };

  private updateOverlayContent = () => {
    if (!this.markerContainerRef || !this.overlayContentRoot) {
      return;
    }

    const { label, isSelected } = this.props;
    const { isHovered } = this.state;
    if (!isSelected && !isHovered) {
      this.overlayContentRoot.render(<></>);
    }

    if (isSelected || (isHovered && !this.props.isLabelHover)) {
      this.markerContainerRef.style.pointerEvents = 'all';
      const infoPane = this.getInfoPane();
      this.overlayContentRoot.render(<MemoryRouter>{infoPane}</MemoryRouter>);
    } else if (label && this.state.showLabelInfo) {
      this.markerContainerRef.style.pointerEvents = this.props.isPlantMarker ? 'auto' : 'none';

      this.overlayContentRoot.render(<MemoryRouter>{label}</MemoryRouter>);

      requestAnimationFrame(() => {
        const labelElement = this.markerContainerRef?.querySelector(
          '[data-test="vehicle-hover-detail-panel"]',
        ) as HTMLElement;
        if (labelElement) {
          const labelWidth = labelElement.offsetWidth;
          const centerOffset = -labelWidth / 2;
          const baseOffset = this.props.pixelOffset;

          if (!this.markerContainerRef) {
            return;
          }

          const adjustedTransform = baseOffset
            ? `translate(${baseOffset.width}px, ${baseOffset.height}px)`
            : `translate(${centerOffset}px, 0px)`;

          this.markerContainerRef.style.transform = adjustedTransform;
        }
      });

      // Handle plant marker hover persistence. Attach once per element;
      // old elements are GC'd with their listeners when the overlay is
      // recreated (e.g. on selection change).
      if (this.props.isPlantMarker) {
        const el = this.markerContainerRef;
        const attachedKey = '__ttmarker_hover_attached__';
        if (!(el as any)[attachedKey]) {
          el.addEventListener('mouseenter', this.cancelHideLabel);
          el.addEventListener('mouseleave', this.scheduleHideLabel);
          (el as any)[attachedKey] = true;
        }
      }
    }
  };

  handleMouseEnter = () => {
    const { isHovered } = this.state;
    const { isRouteHover, isPlantMarker } = this.props;
    if (isHovered) {
      return noop;
    }
    this.setState({ isHovered: true }, () => {
      this.updateOverlayContent();
    });
    if (isRouteHover) return this.onMarkerClick();
    if (isPlantMarker) this.cancelHideLabel();
    return this.showLabel();
  };

  handleMouseLeave = () => {
    const { isHovered } = this.state;
    if (!isHovered) {
      return noop;
    }

    this.setState({ isHovered: false }, () => {
      this.updateOverlayContent();
    });
    const { isRouteHover, isPlantMarker, onCloseClick } = this.props;

    if (isRouteHover) {
      return onCloseClick?.();
    }

    if (isPlantMarker) {
      this.scheduleHideLabel();
      return undefined;
    }

    return this.hideLabel();
  };

  render() {
    const {
      position,
      zIndex = 9,
      icon,
      shadowIcon,
      iconHighlight,
      content,
      manualInfoPosition,
      mapRef,
      isRouteHistory,
      canHover,
    } = this.props;

    const positionLiteral = {
      lat: position.lat ?? 0,
      lng: position.lng ?? 0,
    };

    if (manualInfoPosition) {
      // Force re-center the map for manual positioning
      setTimeout(() => this.centerMap(positionLiteral), 100);
      setTimeout(() => this.centerMap(positionLiteral), 1000);
    }

    return (
      <div style={{ zIndex }} data-route-history={isRouteHistory ? '1' : '0'}>
        <AdvancedMarkerWrapper
          map={mapRef as google.maps.Map}
          position={positionLiteral}
          content={content}
          icon={content ? undefined : icon}
          shadowIcon={content ? undefined : shadowIcon}
          iconHighlight={content ? undefined : iconHighlight}
          zIndex={zIndex}
          onClick={this.onMarkerClick}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          isVisible
          hoverable={canHover}
        />
      </div>
    );
  }
}

type MapRefType = {
  getDiv: () => {
    getBoundingClientRect: () => DOMRect;
  };
  setCenter?: any;
};

export function mapStateToProps(state: ReduxState) {
  return {
    routeHistoryHoverSelection: state.routeHistoryHoverSelection,
  };
}

type ReduxProps = ReturnType<typeof mapStateToProps>;

export interface TTMarkerState {
  showLabelInfo: boolean;
  isHovered: boolean;
}
export interface TTMarkerOwnProps {
  position: {
    statusColor?: string;
    statusText?: string;
    lat?: number;
    lng?: number;
    bearing?: number;
  };
  zIndex?: number;
  infoPaneZIndex?: number;
  icon: any;
  shadowIcon?: any;
  iconHighlight?: any;
  content?: HTMLElement | SVGElement;
  onClick?: VoidFunc;
  isSelected?: boolean;
  onCloseClick?: VoidFunc;
  infoPaneContent?: JSX.Element;
  infoPaneTitle?: JSX.Element | string;
  truckHasTicket?: boolean;
  label?: JSX.Element;
  onLogoutDriver?: VoidFunc;
  mapRef?: MapRefType;
  pixelOffset?: google.maps.Size;
  isExternal?: boolean;
  isLabelHover?: boolean;
  isRouteHover?: boolean;
  isRouteHistory?: boolean;
  isPlantMarker?: boolean;
  manualInfoPosition?: boolean;
  infoPaneOffsetY?: number;
  canHover?: boolean;
}

export type TTMarkerProps = TTMarkerOwnProps & ReduxProps;

export default connect(mapStateToProps)(TTMarker);
